import React, { useState } from 'react';
import { Menu, X, Camera, BarChart3, DollarSign, Calendar, FileText, Users, Settings, Home, ChevronRight, ChevronLeft } from 'lucide-react';

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activePage, setActivePage] = useState('dashboard');

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const menuItems = [
    { id: 'dashboard', label: 'Главная', icon: Home },
    { id: 'monitoring', label: 'Видеонаблюдение', icon: Camera },
    { id: 'schedule', label: 'Планирование', icon: Calendar },
    { id: 'reporting', label: 'Отчетность', icon: FileText },
    { id: 'costs', label: 'Затраты', icon: DollarSign },
    { id: 'analytics', label: 'Аналитика', icon: BarChart3 },
    { id: 'team', label: 'Команда', icon: Users },
    { id: 'settings', label: 'Настройки', icon: Settings },
  ];

  // Mock data for charts
  const costData = [
    { name: 'Янв', costs: 4000 },
    { name: 'Фев', costs: 3000 },
    { name: 'Мар', costs: 2000 },
    { name: 'Апр', costs: 2780 },
    { name: 'Май', costs: 1890 },
    { name: 'Июн', costs: 2390 },
  ];

  const progressData = [
    { phase: 'Фундамент', progress: 100, target: 100 },
    { phase: 'Каркас', progress: 85, target: 90 },
    { phase: 'Крыша', progress: 70, target: 80 },
    { phase: 'Электрика', progress: 45, target: 60 },
    { phase: 'Отделка', progress: 20, target: 30 },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md h-16 flex items-center px-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 mr-2"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="text-xl font-bold text-blue-600">СтройАгент</h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Поиск..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="absolute left-3 top-2.5 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
              А
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside className={`fixed left-0 top-16 h-full bg-white shadow-lg transition-all duration-300 z-40 ${
        isSidebarOpen ? 'w-64' : 'w-16'
      }`}>
        <div className="h-full overflow-y-auto pt-4">
          <nav className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  className={`w-full flex items-center px-3 py-2.5 rounded-lg text-left transition-colors ${
                    activePage === item.id
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  <span className={`ml-3 ${!isSidebarOpen && 'hidden'}`}>
                    {item.label}
                  </span>
                  {!isSidebarOpen && (
                    <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight size={16} />
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 pt-16 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <div className="p-6 space-y-6">
          {/* Header Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Выполнено</p>
                  <p className="text-2xl font-bold text-gray-900">78%</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Затраты</p>
                  <p className="text-2xl font-bold text-gray-900">2,450,000 ₽</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Рабочих</p>
                  <p className="text-2xl font-bold text-gray-900">42</p>
                </div>
              </div>
            </div>
          </div>

          {/* Video Feed and Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Video Feed */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-red-500" />
                Онлайн видео со стройплощадки
              </h2>
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Camera className="w-8 h-8" />
                    </div>
                    <p className="text-lg font-medium">Прямая трансляция</p>
                    <p className="text-sm text-gray-300">Камера 1 - Основная стройка</p>
                  </div>
                </div>
                <div className="absolute top-4 left-4 bg-red-500 text-white px-2 py-1 rounded text-sm font-medium">
                  LIVE
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[1, 2, 3].map((cam) => (
                  <div key={cam} className="aspect-video bg-gray-800 rounded-lg relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-gray-400" />
                    </div>
                    <div className="absolute top-2 left-2 bg-red-500 text-white px-1 py-0.5 rounded text-xs">
                      LIVE
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Прогресс выполнения</h2>
              <div className="space-y-4">
                {progressData.map((phase, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700">{phase.phase}</span>
                      <span className="text-gray-500">{phase.progress}% / {phase.target}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${phase.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cost Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Текущие затраты</h2>
            <div className="h-64 flex items-end space-x-2">
              {costData.map((data, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-300 hover:from-blue-600 hover:to-blue-500"
                    style={{ height: `${(data.costs / 4000) * 200}px` }}
                  ></div>
                  <span className="text-xs text-gray-600 mt-2">{data.name}</span>
                  <span className="text-xs text-gray-500">{data.costs.toLocaleString()} ₽</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Последние события</h2>
            <div className="space-y-4">
              {[
                { action: 'Завершен фундамент', time: '2 часа назад', user: 'Иван Петров' },
                { action: 'Начата кладка стен', time: '5 часов назад', user: 'Сергей Сидоров' },
                { action: 'Поступили материалы', time: '1 день назад', user: 'Анна Козлова' },
                { action: 'Завершена проводка', time: '2 дня назад', user: 'Михаил Волков' },
              ].map((event, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{event.action}</p>
                    <p className="text-xs text-gray-500">{event.user} • {event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;